import {
    ContractWarning,
    CustomerEventWarning,
    DayOffWarning,
    DocumentRetrievalWarning,
    LeadWarning,
    POCServiceWarning,
    ServiceMissingUUIDWarning,
    ServiceWaitingConfirmWarning,
    SubcontractDocumentRetrievalWarning,
    TicketWarning,
    WarningItem,
} from '@/types/warnings'
import { formatNumeral } from '@/utils/function'
import dayjs from 'dayjs'
import { TFunction } from 'i18next'

export interface AlertMetadata {
    label: string
    subtitle: string
    description: string
}

const getTimeDisplay = (alert: WarningItem, t: TFunction) => {
    switch (alert.type) {
        case 'contract_expired':
        case 'contract_expiring': {
            const contractData = alert.data as ContractWarning
            if (alert.type === 'contract_expired') {
                return t('alerts.type.contract_expired.days_overdue', {
                    daysOverdue: formatNumeral(contractData.days_overdue, 0),
                })
            } else {
                return t('alerts.type.contract_expiring.days_remaining', {
                    days: formatNumeral(contractData.days_to_expiry, 0),
                })
            }
        }
        case 'document_retrieval_upcoming':
        case 'document_retrieval_overdue': {
            const documentRetrievalData = alert.data as DocumentRetrievalWarning
            if (alert.type === 'document_retrieval_upcoming') {
                return t(
                    'alerts.type.document_retrieval_upcoming.days_remaining',
                    {
                        days: formatNumeral(
                            documentRetrievalData.days_difference,
                            0
                        ),
                    }
                )
            } else {
                return t(
                    'alerts.type.document_retrieval_overdue.days_overdue',
                    {
                        daysOverdue: formatNumeral(
                            documentRetrievalData.days_difference,
                            0
                        ),
                    }
                )
            }
        }
        default:
            return dayjs(alert.created_at).format('DD/MM/YYYY')
    }
}

export const buildAlertMetadata = (
    alert: WarningItem,
    t: TFunction
): AlertMetadata => {
    switch (alert.type) {
        case 'contract_expired':
        case 'contract_expiring': {
            const contractData = alert.data as ContractWarning
            return {
                label: contractData?.contract_number_id || '',
                subtitle: contractData?.customer_id
                    ? `${contractData?.company_name || contractData?.customer_name || ''} - ${contractData?.customer_id}`
                    : contractData?.company_name ||
                      contractData?.customer_name ||
                      '',
                description: getTimeDisplay(alert, t),
            }
        }
        case 'document_retrieval_upcoming':
        case 'document_retrieval_overdue': {
            const documentRetrievalData = alert.data as DocumentRetrievalWarning
            return {
                label: documentRetrievalData?.contract_number_id || '',
                subtitle:
                    documentRetrievalData?.company_name ||
                    documentRetrievalData?.customer_name ||
                    '',
                description: getTimeDisplay(alert, t),
            }
        }
        case 'dayoff_pending': {
            const dayOffData = alert.data as DayOffWarning
            return {
                label: dayOffData?.employee_name,
                subtitle: `${dayOffData?.number_of_day_off} ${t('alerts.dayoff.days')}`,
                description: getTimeDisplay(alert, t),
            }
        }
        case 'poc_service_overdue':
        case 'poc_service_upcoming': {
            const pocServiceData = alert.data as POCServiceWarning
            return {
                label: `[ID: ${pocServiceData?.service_id}] - ${pocServiceData?.service_name}`,
                subtitle:
                    pocServiceData?.company_name ||
                    pocServiceData?.customer_name ||
                    '',
                description: alert.description,
            }
        }
        case 'subcontract_document_retrieval_overdue':
        case 'subcontract_document_retrieval_upcoming': {
            const subcontractData =
                alert.data as SubcontractDocumentRetrievalWarning
            return {
                label: `[ID: ${subcontractData?.subcontract_id}] - ${subcontractData?.subcontract_code}`,
                subtitle: subcontractData?.subcontract_code || '',
                description: alert.description,
            }
        }
        case 'service_waiting_confirm': {
            const serviceWaitingConfirmData =
                alert.data as ServiceWaitingConfirmWarning
            return {
                label: `[ID: ${serviceWaitingConfirmData?.service_id}] - ${serviceWaitingConfirmData?.service_name}`,
                subtitle:
                    serviceWaitingConfirmData?.company_name ||
                    serviceWaitingConfirmData?.customer_name ||
                    '',
                description: alert.description,
            }
        }
        case 'service_missing_uuid': {
            const serviceMissingUUIDData =
                alert.data as ServiceMissingUUIDWarning
            return {
                label: `[ID: ${serviceMissingUUIDData?.service_id}] - ${serviceMissingUUIDData?.service_name}`,
                subtitle:
                    serviceMissingUUIDData?.company_name ||
                    serviceMissingUUIDData?.customer_name ||
                    '',
                description: alert.description,
            }
        }
        case 'lead_unassigned':
        case 'lead_stale': {
            const leadData = alert.data as LeadWarning
            return {
                label:
                    leadData?.contact_source_name ||
                    leadData?.contact_source_email ||
                    `Lead #${leadData?.contact_source_id}`,
                subtitle: [
                    leadData?.contact_source_company,
                    leadData?.contact_source_channel,
                ]
                    .filter(Boolean)
                    .join(' - '),
                description: `${leadData?.days_pending} ${t('days')}`,
            }
        }
        case 'ticket_escalated': {
            const ticketData = alert.data as TicketWarning
            // Without this case the default below would give every escalated
            // row the same label, since alert.title is one fixed sentence for
            // the whole type. The ticket number and who raised the flag are the
            // two things that tell the rows apart.
            return {
                label: [
                    `#${ticketData?.ticket_id}`,
                    ticketData?.ticket_type_title || ticketData?.ticket_content,
                ]
                    .filter(Boolean)
                    .join(' · '),
                subtitle: [
                    ticketData?.company_name || ticketData?.customer_name,
                    ticketData?.escalation_reason,
                ]
                    .filter(Boolean)
                    .join(' — '),
                description: [
                    ticketData?.escalated_by_name,
                    getTimeDisplay(alert, t),
                ]
                    .filter(Boolean)
                    .join(' · '),
            }
        }
        case 'customer_event_upcoming': {
            const eventData = alert.data as CustomerEventWarning
            const eventType = t(
                `customer:calendar_events.type_${eventData?.event_type}`
            )
            const date = dayjs(eventData?.upcoming_date).format('DD/MM')
            const timeDescription =
                eventData?.days_until === 0
                    ? t('customer:calendar_events.alert_today')
                    : eventData?.days_until === 1
                      ? t('customer:calendar_events.alert_tomorrow')
                      : t('customer:calendar_events.alert_days_until', {
                            count: eventData?.days_until ?? 0,
                        })

            return {
                label:
                    eventData?.company_name ||
                    eventData?.customer_name ||
                    alert.title,
                subtitle: `${eventType} · ${date}`,
                description: timeDescription,
            }
        }
        default:
            return {
                label: alert.title,
                subtitle: alert.description,
                description: getTimeDisplay(alert, t),
            }
    }
}
